import { FC, PropsWithChildren, useCallback, useMemo } from 'react';

import {
  createAuthenticationAdapter,
  RainbowKitAuthenticationProvider,
  RainbowKitProvider,
} from '@rainbow-me/rainbowkit';
import { useQueryClient } from '@tanstack/react-query';
import { addMinutes } from 'date-fns/addMinutes';
import { isAddress } from 'viem';
import { createSiweMessage } from 'viem/siwe';

import { fetchNonce, signInWithWallet } from '@web/utils/auth/service';
import { chains } from '@web/utils/rainbowkit/chains';
import { trpcClient } from '@web/utils/trpc/trpcClient';

interface ICallbacks {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface IVerify {
  (message: string, signature: string): Promise<void>;
}

interface IGenericProps extends ICallbacks {
  verify: IVerify;
}

const GenericProvider: FC<PropsWithChildren<IGenericProps>> = ({
  children,
  verify,
  onSuccess,
  onError,
}) => {
  const authenticationAdapter = useMemo(
    () =>
      createAuthenticationAdapter({
        // fetchNonce stashes the HMAC signature for SIWE step 2 to use.
        getNonce: fetchNonce,
        createMessage: ({ nonce, address, chainId }) => {
          if (!isAddress(address)) {
            throw new Error('Siwe message requires a valid address.');
          }
          return {
            domain: window.location.host,
            address,
            issuedAt: new Date(),
            expirationTime: addMinutes(new Date(), 5),
            statement: 'Sign in with Ethereum to the app.',
            uri: window.location.origin,
            version: '1' as const,
            chainId,
            nonce,
          };
        },
        getMessageBody: ({ message }) => createSiweMessage(message),
        signOut: () => Promise.resolve(),
        verify: async ({ message, signature }) => {
          try {
            await verify(createSiweMessage(message), signature);
            onSuccess?.();
          } catch (err) {
            console.error(err);
            onError?.(err);
            return false;
          }
          return true;
        },
      }),
    [onSuccess, onError, verify],
  );

  return (
    <RainbowKitAuthenticationProvider
      status="unauthenticated"
      adapter={authenticationAdapter}
    >
      <RainbowKitProvider initialChain={chains.ethereum}>
        {children}
      </RainbowKitProvider>
    </RainbowKitAuthenticationProvider>
  );
};

export const SignInRainbowKitProvider: FC<PropsWithChildren<ICallbacks>> = (
  props,
) => <GenericProvider verify={signInWithWallet} {...props} />;

export const ConnectWalletRainbowKitProvider: FC<
  PropsWithChildren<ICallbacks>
> = (props) => {
  const queryClient = useQueryClient();
  const verify = useCallback<IVerify>(
    async (message, signature) => {
      // connectWallet edge fn isn't implemented in supabase-native phase
      // yet (see procedures.ts). When added, this will mirror signInWithWallet.
      await trpcClient.auth.connectWallet.mutate({ message, signature });
      await queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
    [queryClient],
  );

  return <GenericProvider verify={verify} {...props} />;
};
